<template>
  <AuthLayout :max-width="480">
    <template v-if="sent">
      <div class="text-center">
        <v-icon color="success" size="56" class="mb-4"
          >mdi-email-check-outline</v-icon
        >
        <v-card-title class="justify-center mb-2"
          >Ellenőrizze postafiókját</v-card-title
        >
        <v-card-text>
          Ha ehhez az email címhez tartozik fiók, elküldtük a jelszó
          visszaállításához szükséges linket. A link 1 óráig érvényes.
        </v-card-text>
        <v-btn color="primary" variant="tonal" :to="loginPath" class="mt-2"
          >Vissza a bejelentkezéshez</v-btn
        >
      </div>
    </template>

    <template v-else>
      <v-card-title class="mb-2">Elfelejtett jelszó</v-card-title>
      <v-form ref="formRef" @submit.prevent="submit">
        <v-card-text>
          <p class="mb-4 text-medium-emphasis">
            Adja meg fiókja email címét, és küldünk egy linket, amellyel új
            jelszót adhat meg.
          </p>
          <v-text-field
            v-model="email"
            label="Email cím"
            type="email"
            density="compact"
            variant="outlined"
            prepend-inner-icon="mdi-email-outline"
            autofocus
            :rules="[rules.required, rules.email]"
          />
        </v-card-text>

        <v-card-actions>
          <v-btn variant="text" :to="loginPath">Mégse</v-btn>
          <v-spacer />
          <v-btn type="submit" color="primary" :loading="submitting"
            >Link küldése</v-btn
          >
        </v-card-actions>
      </v-form>
    </template>
  </AuthLayout>
</template>

<script setup lang="ts">
import AuthLayout from "@/components/layout/AuthLayout.vue";
import { computed, ref } from "vue";
import { useRoute } from "vue-router";
import { PasswordResetService } from "@/services/PasswordResetService";
import { useToast } from "@/services/useToast";

const route = useRoute();
const passwordResetService = new PasswordResetService();
const { showToast } = useToast();

const email = ref("");
const sent = ref(false);
const submitting = ref(false);
const formRef = ref<{ validate: () => Promise<{ valid: boolean }> } | null>(
  null
);

// Admins arrive here from /admin/login and have to go back to their own login page.
const loginPath = computed(() =>
  route.query.admin !== undefined ? "/admin/login" : "/login"
);

const rules = {
  required: (v: string) => !!v?.trim() || "Kötelező mező",
  email: (v: string) => /.+@.+\..+/.test(v ?? "") || "Érvénytelen email cím",
};

async function submit() {
  const { valid } = await formRef.value!.validate();
  if (!valid) return;

  submitting.value = true;
  try {
    await passwordResetService.requestReset(email.value.trim());
    sent.value = true;
  } catch (e: any) {
    if (e?.response?.status === 429) {
      showToast(
        "Túl sok próbálkozás. Kérjük, várjon néhány percet, majd próbálja újra.",
        "error"
      );
    } else {
      showToast(
        "A kérés elküldése sikertelen. Kérjük, próbálja újra.",
        "error"
      );
    }
  } finally {
    submitting.value = false;
  }
}
</script>
